import { createContext, useContext, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginInput, RegisterInput, UserDTO } from '@rg/shared';
import * as authApi from '@/api/auth';
import { ApiError } from '@/lib/api-client';

interface AuthState {
  user: UserDTO | null;
  isLoading: boolean;
  login: (i: LoginInput) => Promise<UserDTO>;
  register: (i: RegisterInput) => Promise<UserDTO>;
  loginWithGoogle: (credential: string) => Promise<UserDTO>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
const ME_KEY = ['auth', 'me'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return (await authApi.me()).user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const setUser = (u: UserDTO | null) => qc.setQueryData(ME_KEY, u);
  const loginM = useMutation({ mutationFn: authApi.login, onSuccess: (d) => setUser(d.user) });
  const registerM = useMutation({
    mutationFn: authApi.register,
    onSuccess: (d) => setUser(d.user),
  });

  const googleM = useMutation({
    mutationFn: authApi.googleLogin,
    onSuccess: (d) => setUser(d.user),
  });

  const value: AuthState = {
    user: meQuery.data ?? null,
    isLoading: meQuery.isLoading,
    login: async (i) => (await loginM.mutateAsync(i)).user,
    register: async (i) => (await registerM.mutateAsync(i)).user,
    loginWithGoogle: async (c) => (await googleM.mutateAsync(c)).user,
    logout: async () => {
      await authApi.logout().catch(() => undefined);
      setUser(null);
      qc.removeQueries({ queryKey: ['my-scans'] });
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
